import { mediaFragment, responsiveMediaFragment } from './mediaFragments.gql'
import {
  genericStructuredTextFragment,
  inlineCTARecordFragment,
  titleBlockFragment,
  singleImageRecordFragment,
  customThemeFragment,
} from './structuredText.gql'

export const seoFragment = `meta: _seoMetaTags {
    attributes
    content
    tag
  }`

export const ctaFragment = `
  ctaLink
  ctaCopy
  showCta
`

export const internalLinkFragment = `
  ... on HomepageRecord {
    id
    _modelApiKey
  }
  ... on FilterPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on BaseProductRecord {
    id
    slug: url
    _modelApiKey
  }
  ... on AthleteRecord {
    id
    slug
    _modelApiKey
  }
  ... on ArticleCategoryRecord {
    id
    slug
    _modelApiKey
  }
  ... on ArticleRecord {
    id
    slug
    _modelApiKey
    category {
      slug
    }
  }
  ... on CareersPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on CompetitionsPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on CompositePdpRecord {
    id
    slug
    _modelApiKey
  }
  ... on ExploreLandingRecord {
    id
    slug
    _modelApiKey
  }
  ... on GiftingPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on GuaranteeRegistrationPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on LandingPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on PartnerPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on SustainabilityPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on SupportPageRecord {
    id
    slug
    _modelApiKey
  }
  ... on LegacyCollectionRecord {
    slug
    id
    _modelApiKey
  }
`

export const splitTestVariantFragment = `
  splitTestVariant {
    id
    variantId
    splitTestKey
  }
`

export const typeFragment = 'type: _modelApiKey'
export const displayFragment = `
  visibility
  skuPattern
  ${splitTestVariantFragment}
`

export const contentLoopSectionFormattingBlockFragment = `
  contentLoopSectionFormatting {
    paddingTop
    paddingBottom
    background
  }
`

export const headingFragment = `
  ${typeFragment}
  ${displayFragment}
  title
  titleGradient
  titleSize
  titleTag
  subtitle
  subtitleTag
  subtitleGradient
  fullWidthSubtitle
  copy
  copySize
  copyTag
  fullWidthCopy
  showCta
  ctaCopy
  ctaLink
  centerContent
  ${contentLoopSectionFormattingBlockFragment}
`

export const headingRecord = `
  ... on HeadingRecord {
    id
    ${headingFragment}
  }`

export const standfirstRecord = `
  ... on StandfirstRecord {
    id
    ${typeFragment}
    ${displayFragment}
    title
    titleTag
    largeTitle
    subtitle
    fullWidth
    ${contentLoopSectionFormattingBlockFragment}
  }`

export const dividerRecord = `
  ... on DividerRecord {
    id
    ${typeFragment}
    ${displayFragment}
  }`

export const bannerFragment = `
  ${typeFragment}
  ${displayFragment}
  ${responsiveMediaFragment()}
  ${responsiveMediaFragment('mobileImage')}
  overlayOpacity
  mobileOverlayOpacity
  ${ctaFragment}
  displayTitle
  titleSize
  titleTag
  copy
  useRichText
  richTextCopy
  halfWidthCopy
  fullWidthCopy
  inlineCopy
  primaryColour
  headingGradient
  fullWidth
  ctaGradient
  bannerHeightAuto
  matchHeightToContent
  minHeight
  bottomPadding
  disableParallax
  contentAlignment
  hidden
  displayOnlyWithUrlParam
  features {
    id
    icon {
      url
    }
    text
  }
  ${contentLoopSectionFormattingBlockFragment}
`

export const bannerRecord = `
  ... on BannerRecord {
    id
    ${bannerFragment}
  }`

export const customisedProductFragment = `
  _modelApiKey
  sku
  title
  subTitle
  frameSwatchOverride
  lensSwatchOverride
  swatchIconOverride {
    url(imgixParams: {w: "50", h: "50"})
  }
  swatchCornerIcon {
    id
    url
    mimeType
    title
  }
  swatchCornerStyle
  lensPart
  limitedEdition
  partsLimitedEdition
  stockManagedPart
  variantGroup
`

export const stackFragment = `
  ${typeFragment}
  ${mediaFragment()}
  ${ctaFragment}
  link
  title
  titleSize
  titleTag
  subtitle
  copy
  copySize
  ctaType
  ctaStyle
  aspectRatio
  fullHeight
`

export const productCardFragment = `
  ${typeFragment}
  showTag
  tagCopy
  imageNumber
  useBaseProduct
  showSwatchSelector
  showReviewStars
  isPrescriptionProducts
  titleTag
  products {
    ${typeFragment}
    sku
    title
    subTitle
    frameSwatchOverride
    lensSwatchOverride
    swatchIconOverride {
      url(imgixParams: {w: "50", h: "50"})
    }
    lensPart
    limitedEdition
    ${mediaFragment('logo', '{ w: "200", h: "100", fit: fill }')}
    stockManagedPart
    theme
    baseProduct {
      sku
      swatchIconOverride {
        url(imgixParams: {w: "50", h: "50"})
      }
      theme
      promoMessaging {
        enabled
        tagText
        theme {
          themeType
        }
      }
    }
  }
  baseProduct {
    ${typeFragment}
    sku
  }
  onGiftingPage
  showGiftCard
  seeMoreLinkOverride
`

export const sustainabilityBlockFragment = `
  id
  customClass
  backgroundColour
  ${mediaFragment('desktopBackground', '{ w: "1400", h: "1300", fit: clip }')}
  ${mediaFragment('mobileBackground', '{ w: "1000", h: "900", fit: clip }')}
  ${mediaFragment('icon', '{ w: "320", h: "200", fit: clip }')}
  iconPosition
  heading {
    ... on HighlighterHeadingRecord {
      __typename
      _modelApiKey
      id
      highlightType
      heading {
        value
      }
    }
  }
  modalContent {
    slug
  }
  showSubheading
  subheading {
    blocks
    value
  }
  subheadingColour
  ${contentLoopSectionFormattingBlockFragment}
`

export const dataCaptureForm = `
  config {
    id
    slug
    requireEmailVerification
  }
  ctaCopy
  verifyCopy
  successMessage {
    value
    blocks {
      id
      __typename
      copy
      style
      target
    }
  }
  terms {
    value
    links {
      __typename
      slug
      _modelApiKey
      id
    }
  }
  showMailingListSubscribeCheckbox
  requireMailingListSubscription
  mailingListCopy
`

export const dataCaptureBlock = `
  id
  ${typeFragment}
  title
  intro
  ctaCopy
  showMailingListSubscribeCheckbox
  requireMailingListSubscription
  mailingListCopy
  verifyCopy
  successMessage {
    value
    blocks {
      id
      __typename
      copy
      style
      target
    }
  }
  successReplacesForm
  terms {
    value
    links {
      __typename
      slug
      _modelApiKey
      id
    }
  }
  ${responsiveMediaFragment('media', '{ w: "1000", h: "900", fit: clip }')}
  mediaVisibility
  borderVisibility
  config {
    id
    slug
    requireEmailVerification
  }
  ${contentLoopSectionFormattingBlockFragment}
`

export const modalSidebarFragment = `
  ... on DialogRecord {
    ${typeFragment}
    id
    slug
  }
  ... on SidebarPageRecord {
    ${typeFragment}
    id
    slug
  }
`

export const linkBlock = `
  internalLink {
    ${internalLinkFragment}
  }
  internalLinkQueryParams
  modalSidebar {
    ${modalSidebarFragment}
  }
`

export const indicatorBlock = `
  indicatorColour
  indicatorText
`

export const buttonBlock = `
  id
  text
  style
  link {
    ${linkBlock}
  }
`

export const countdownBlock = `
  ${typeFragment}
  datetime
  display
  finishedMessage
`

export const liveVisitorBlock = `
  ${typeFragment}
  text
  display
  minimumNumber
`

export const videoRecord = `
  ... on VideoRecord {
    ${typeFragment}
    ${displayFragment}
    id
    loop
    muted
    autoPlay
    previewStartTime
    fullWidth
    video {
      height
      width
      title
      thumbhash
      video {
        muxPlaybackId
      }
    }
  }
`

export const mediaItemFragment = `
  mediaType
  ${responsiveMediaFragment('image')}
  ${responsiveMediaFragment('reel')}
  speed
  video {
    ${videoRecord}
  }
  aspectRatio
`

export const mediaOverlayFragment = `
  mediaType
  video {
    ${videoRecord}
  }
  portraitVideo {
    ${videoRecord}
  }
  externalVideo {
    thumbnailUrl
    height
    width
    provider
    providerUid
  }
  text
  ${responsiveMediaFragment('image', '{ w: "240", h: "240" }')}
  theme {
    ${customThemeFragment}
  }
`

export const interactiveListItemFragment = `
  id
  ${typeFragment}
  title
  subtitle
  description
  media {
    ${mediaItemFragment}
  }
`

export const stickyScrollListItemFragment = `
  id
  ${typeFragment}
  content {
    ... on TitleBlockRecord {
      ${typeFragment}
      id
      ${titleBlockFragment}
    }
    ... on ButtonGroupRecord {
      id
      ${typeFragment}
      buttons {
        ${buttonBlock}
      }
    }
    ... on DescriptionRecord {
      id
      ${typeFragment}
      description(markdown: true)
    }
    ... on SubtitleRecord {
      id
      ${typeFragment}
      subtitle
    }
  }
`

export const cardFragment = `
  ${typeFragment}
  id
  content {
    ... on TitleBlockRecord {
      ${typeFragment}
      id
      ${titleBlockFragment}
    }
    ... on ButtonGroupRecord {
      id
      ${typeFragment}
      buttons {
        ${buttonBlock}
      }
    }
    ... on DescriptionRecord {
      id
      ${typeFragment}
      description
    }
    ... on IconRecord {
      ${typeFragment}
      icon
    }
    ... on SingleImageRecord {
      ${typeFragment}
      ${singleImageRecordFragment}
    }
    ... on HighlighterHeadingRecord {
      ${typeFragment}
      _modelApiKey
      id
      highlightType
      size
      heading {
        value
      }
    }
  }
  link {
    ${linkBlock}
  }
  ${responsiveMediaFragment('image', '{ w: "1200", h: "1200" }')}
  ${responsiveMediaFragment('mobileImage', '{ w: "720", h: "720" }')}
  alignment
  bgColour {
    hex
  }
  colour
  iconButton
  justification
  linkText
  scrim
  shadow
  subtitle
  subtitleStyle
  linkedTheme {
    ${customThemeFragment}
  }
  trackingId
`

export const mediaHeaderBlock = `
  id
  ${typeFragment}
  horizontalAlignment
  desktopMinHeight
  mobileMinHeight
  foregroundImagesDesktopPosition
  content {
    ${genericStructuredTextFragment}
  }
  ${responsiveMediaFragment('backgroundImage', '{ w: "2000", h: "1000" }')}
  ${responsiveMediaFragment('desktopImage1', '{ w: "600", h: "600" }')}
  desktopImage1Link {
    ${linkBlock}
  }
  ${responsiveMediaFragment('desktopImage2', '{ w: "600", h: "600" }')}
  desktopImage2Link {
    ${linkBlock}
  }
  ${responsiveMediaFragment('mobileImage', '{ w: "600", h: "600" }')}
  mobileImageLink {
    ${linkBlock}
  }
  backgroundVideo {
    ${videoRecord}
  }
  backgroundMediaOverlay
  backgroundMediaLink {
    ${linkBlock}
  }
  trackingId
  variation
  theme {
    ${customThemeFragment}
  }
`

const scrollRevealTitle = `
  titleTextReveal {
    value
  }
`

export const textRevealSection = `
  id
  ${typeFragment}
  ${contentLoopSectionFormattingBlockFragment}
  content {
    ${scrollRevealTitle}
  }
  textHighlightTheme {
    ${customThemeFragment}
  }
`

export const productCalloutSection = `
  id
  ${typeFragment}
  ${contentLoopSectionFormattingBlockFragment}
  pretitleOverride
  sectionTitle
  buttonText
  display
  theme {
    ${customThemeFragment}
  }
  products {
    model {
      name
      sku
      subTitle
    }
    heroDesign {
      enabled
      parts {
        sku
      }
    }
  }
`

const mediaHeroContent = `
  title
  subtitle
  mediaHeroContentButtons {
    linkText
    style
    colour
    link {
      ${linkBlock}
    }
}`

const mediaHeroImageItem = `
  id
  ${responsiveMediaFragment('heroImage', '{ w: 800 }')}
  ${responsiveMediaFragment('heroImageDesktop', '{ w: 2440 }')}
  mediaHeroContent {
    ${mediaHeroContent}
  }
  theme {
    ${customThemeFragment}
  }
  scrim
`

const mediaHeroVideoItem = `
  id
  heroVideo {
    ${videoRecord}
  }
  heroVideoDesktop {
    ${videoRecord}
  }
  mediaHeroContent {
    ${mediaHeroContent}
  }
  theme {
    ${customThemeFragment}
  }
  scrim
`

export const mediaHeroSection = `
  id
  ${typeFragment}
  aspectRatio
  aspectRatioDesktop
  mediaHeroType {
    ... on MediaHeroCarouselRecord {
      id
      ${typeFragment}
      mediaHeroItems {
        ... on MediaHeroImageItemRecord {
          ${mediaHeroImageItem}
        }
        ... on MediaHeroVideoItemRecord {
          ${mediaHeroVideoItem}
        }
      }
    }
    ... on MediaHeroStaticRecord {
      id
      ${typeFragment}
      mobileLayout
      mediaHeroItems {
        ... on MediaHeroImageItemRecord {
          ${mediaHeroImageItem}
        }
        ... on MediaHeroVideoItemRecord {
          ${mediaHeroVideoItem}
        }
      }
    }
    ... on MediaHeroSingleRecord {
      id
      ${typeFragment}
      mediaHeroItems {
        ... on MediaHeroImageItemRecord {
          ${mediaHeroImageItem}
        }
        ... on MediaHeroVideoItemRecord {
          ${mediaHeroVideoItem}
        }
      }
    }
  }
`

export const brandHeroSection = `
  id
  ${typeFragment}
  brandHeroType {
    ... on MediaHeroSingleRecord {
      id
      ${typeFragment}
      mediaHeroItems {
        ... on MediaHeroImageItemRecord {
          ${mediaHeroImageItem}
        }
        ... on MediaHeroVideoItemRecord {
          ${mediaHeroVideoItem}
        }
      }
    }
  }
  brandHeroImageLinkBlocks {
    id
    ${responsiveMediaFragment('desktopImage')}
    ${responsiveMediaFragment('mobileImage')}
    link {
      ${linkBlock}
    }
    linkText
  }
`

export const formHeroSection = `
  id
  ${typeFragment}
  indicator {
    ${indicatorBlock}
  }
  title
  subtitle {
    value
  }
  buttons {
    ${buttonBlock}
  }
  dataCaptureForm {
    ${dataCaptureForm}
  }
  ${responsiveMediaFragment('image')}
  video {
    ${videoRecord}
  }
`

export const filmStripInteractiveHero = `
  id
  ${typeFragment}
  background
  theme {
    ${customThemeFragment}
  }
  videoLandscape {
    ${videoRecord}
  }
  videoPortrait {
    ${videoRecord}
  }
  ${responsiveMediaFragment('imageLandscape', '{ w: "1920"}')}
  ${responsiveMediaFragment('imagePortrait', '{ h: "1440"}')}
  fullVideo {
    ${mediaOverlayFragment}
  }
  textSize
  textContent {
    title
    subtitle
    buttons: mediaHeroContentButtons {
      linkText
      style
      colour
      link {
        ${linkBlock}
      }
    }
  }
  additionalMedia {
    ${mediaItemFragment}
  }
  trackingId
`

export const stickyScrollListSection = `
  id
  ${typeFragment}
  content {
    ... on StickyScrollListItemRecord {
      ${typeFragment}
      ${stickyScrollListItemFragment}
    }
    ... on MediaItemRecord {
      ${typeFragment}
      ${mediaItemFragment}
    }
  }
  titleContent {
    ... on TitleBlockRecord {
      ${typeFragment}
      id
      ${titleBlockFragment}
    }
    ... on ButtonGroupRecord {
      id
      ${typeFragment}
      buttons {
        ${buttonBlock}
      }
    }
    ... on DescriptionRecord {
      id
      ${typeFragment}
      description
    }
  }
  titleBackground {
    ${mediaItemFragment}
  }
  titleAlignment
  theme {
    ${customThemeFragment}
  }
  ${contentLoopSectionFormattingBlockFragment}
  trackingId
`

export const emailSignupBlock = `
  id
  ${typeFragment}
  ${responsiveMediaFragment()}
  video {
    ${videoRecord}
  }
  headingText
  headingSize
  headingStyle
  title {
    ${titleBlockFragment}
  }
  body {
    value
    blocks
    links
  }
  formEmailPlaceholder
  formCtaText
  formOptIn
  formOptInText
  formSuccess {
    value
    blocks {
      ${inlineCTARecordFragment}
    }
    links
  }
  stylingBackgroundColours {
    colour {
      hex
    }
  }
  stylingBackgroundGradientAngle
  stylingLayout
  stylingTextStyle
  stylingInputStyle
  stylingButton
  stylingButtonStyle
  trackingId
  klaviyoListId
  ${contentLoopSectionFormattingBlockFragment}
`

export const marketingRowBlock = `
  id
  ${typeFragment}
  layout
  layoutDesktop
  trackingId
  aspectRatio
  cards {
    ${cardFragment}
  }
  ${contentLoopSectionFormattingBlockFragment}
`

export const stickyCtaButtonBlock = `
  id
  ${typeFragment}
  text
  colour
  trigger
  icon {
    icon
  }
  link {
    ${linkBlock}
  }
  theme {
    ${customThemeFragment}
  }
  trackingId
`

export const credibilityRowBlock = `
  id
  ${typeFragment}
  title
  subtitle
  source
  trackingId
  columns {
    id
    items {
      aspectRatio
      date
      id
      logo: ${responsiveMediaFragment('image', '{ w: "120", h: "120" }')}
      profilePicture: ${responsiveMediaFragment('image', '{ w: "120", h: "120" }')}
      bgImage: ${responsiveMediaFragment('image', '{ w: "800", h: "800" }')}
      inlineImage: ${responsiveMediaFragment('image', '{ w: "600", h: "600" }')}
      name
      platform
      postType
      quote
      secondary
      starRating
      subtitle
      username
      video {
        ${videoRecord}
      }
    }
  }
  ${contentLoopSectionFormattingBlockFragment}
`

export const impactCarouselBlock = `
  id
  ${typeFragment}
  trackingId
  layout
  purpose
  content: title {
    ${genericStructuredTextFragment}
  }
  ${responsiveMediaFragment('backgroundImage', '{ w: "800", h: "800" }')}
  exploreCategories {
    id
  }
  cards {
    id
    ${typeFragment}
    trackingId
    title
    description
    ${responsiveMediaFragment('media', '{ w: "500", h: "500" }')}
    link {
      ${linkBlock}
    }
  }
  ${contentLoopSectionFormattingBlockFragment}
`

export const InstagramShopBlock = `
  id
  ${typeFragment}
  trackingId
  posts
  rows
  content {
    ${genericStructuredTextFragment}
  }
  instashopProducts {
    id
  }
  ${contentLoopSectionFormattingBlockFragment}
`

export const InteractiveListBlock = `
  id
  ${typeFragment}
  title
  media {
    ${mediaItemFragment}
  }
  items {
    ${interactiveListItemFragment}
  }
  ${contentLoopSectionFormattingBlockFragment}
`

export const dataCaptureBlockRecord = `
  ... on DataCaptureBlockRecord {
    ${dataCaptureBlock}
  }
`

export const mediaHeaderRecord = `
  ... on MediaHeaderRecord {
    ${mediaHeaderBlock}
  }
`

export const textRevealRecord = `
  ... on TextRevealSectionRecord {
    ${textRevealSection}
  }
`

export const productCalloutSectionRecord = `
  ... on ProductCalloutSectionRecord {
    ${productCalloutSection}
  }
`

export const mediaHeroRecord = `
  ... on MediaHeroRecord {
    ${mediaHeroSection}
  }
`

export const brandHeroRecord = `
  ... on BrandHeroRecord {
    ${brandHeroSection}
  }
`

export const formHeroRecord = `
  ... on FormHeroRecord {
    ${formHeroSection}
  }
`

export const emailSignupRecord = `
  ... on EmailSignupRecord {
    ${emailSignupBlock}
  }
`

export const filmStripInteractiveHeroRecord = `
  ... on FilmStripInteractiveHeroRecord {
    ${filmStripInteractiveHero}
  }
`

export const stickyScrollListRecord = `
  ... on StickyScrollListRecord {
    ${stickyScrollListSection}
  }
`

export const marketingRowRecord = `
  ... on MarketingRowRecord {
    ${marketingRowBlock}
  }
`
export const credibilityRowRecord = `
  ... on CredibilityRowRecord {
    ${credibilityRowBlock}
  }
`

export const stickyCtaButtonRowRecord = `
  ... on StickyCtaButtonRecord {
    ${stickyCtaButtonBlock}
  }
`

export const storeLocatorRowRecord = `
  ... on StoreLocatorRowRecord {
    id
    ${typeFragment}
    locationSlug
    ${contentLoopSectionFormattingBlockFragment}
  }
`

export const cardScrollerRowRecord = `
  ... on CardScrollerRecord {
    id
    ${typeFragment}
    pretitle
    title {
      ${scrollRevealTitle}
    }
    backgroundColour
    cards {
      ... on CardRecord {
        ${cardFragment}
      }
      ... on CardScrollerCardRecord {
        id
        ${typeFragment}
        cardType
        cardBackgroundColour
        paragraphTextContent
        paragraphTextFooterTitle
        paragraphTextFooterSubtitle
        ${responsiveMediaFragment('cardBackgroundImage')}
        ${responsiveMediaFragment('image')}
      }
    }
    ${contentLoopSectionFormattingBlockFragment}
  }
`

export const impactCarouselRecord = `
  ... on ImpactCarouselRecord {
    ${impactCarouselBlock}
  }
`

export const instagramShopRecord = `
  ... on InstagramShopRecord {
    ${InstagramShopBlock}
  }
`

export const interactiveListBlockRecord = `
  ... on InteractiveListRecord {
    ${InteractiveListBlock}
  }
`

export const multiColumnRecord = `
  ... on MultiColumnRecord {
    id
    ${typeFragment}
    ${displayFragment}
    columns {
      ... on BlankRecord {
        id
        ${typeFragment}
      }
      ... on ProductCardRecord {
        id
        ${productCardFragment}
      }
      ... on StackRecord {
        id
        ${stackFragment}
      }
      ... on BannerCardRecord {
        id
        ${typeFragment}
        ${mediaFragment()}
        displayTitle
        titleSize
        primaryColour
        copy
      }
      ... on ContentBlockRecord {
        id
        ${typeFragment}
        model {
          ${typeFragment}
          ${sustainabilityBlockFragment}
        }
      }
    }
    ${contentLoopSectionFormattingBlockFragment}
  }`

export const richTextRecord = `
  ... on RichTextRecord {
    ${typeFragment}
    id
    content
    matchPaddingToBanner
    ${ctaFragment}
    ctaGradient
    ${displayFragment}
    ${contentLoopSectionFormattingBlockFragment}
}`

export const displayPriceOverrideFragment = `
  percentageOff
`

export const carouselRecord = `
  ... on CarouselRecord {
    ${typeFragment}
    id
    content {
      ${genericStructuredTextFragment}
    }
    globalPriceOverride {
      ${displayPriceOverrideFragment}
    }
    trackingId
    items {
      ... on CarouselItemRecord {
        ${typeFragment}
        id
        title
        titleHtmlTag
        subtitle
        target
        overlayOpacity
        ${mediaFragment()}
      }
      ... on ProductCardRecord {
        ${productCardFragment}
      }
    }
    ${contentLoopSectionFormattingBlockFragment}
  }`

export const centeredCarouselRecord = `
  ... on CenteredCarouselRecord {
    ${typeFragment}
    id
    background
    trackingId
    titleBlock {
      ${titleBlockFragment}
    }
    items {
      ${typeFragment}
      id
      title
      cta
      trackingId
      internalLink {
        ${internalLinkFragment}
      }
      ${mediaFragment()}
      imageColours: image {
        colors {
          hex
        }
      }
      backgroundColours
      customColours {
        ${typeFragment}
        colour {
          hex
        }
      }
    }
    ${contentLoopSectionFormattingBlockFragment}
  }`

export const styledBannerFragment = `
  ${typeFragment}
  id
  backgroundColour
  condensedView
  style
  ${responsiveMediaFragment()}
  ${responsiveMediaFragment('mobileImage')}
  content {
    blocks
    value
  }
  textColour
  ${contentLoopSectionFormattingBlockFragment}
`

export const styledBannerRecord = `
  ... on StyledBannerRecord {
    ${styledBannerFragment}
  }`

export const sustainabilityBlockRecord = `
  ... on SustainabilityBlockRecord {
    ${typeFragment}
    id
    ${sustainabilityBlockFragment}
  }
`

export const formBlockRecord = `
  ... on FormBlockRecord {
    ${typeFragment}
    id
    formId
    ${contentLoopSectionFormattingBlockFragment}
  }
`

export const faqBlockRecord = `
  ... on FaqBlockRecord {
    ${typeFragment}
    id
    uid
    title
    footerCopy
    articles {
      id
      title
      bodyCopy
    }
    ${contentLoopSectionFormattingBlockFragment}
  }
`

export const anchorButtonRowRecord = `
  ... on AnchorButtonRowRecord {
    ${typeFragment}
    id
    sticky
    anchorButtonContainer {
      ... on AnchorButtonRecord {
        id
        buttonText
        sectionId
      }
      ${inlineCTARecordFragment}
    }
    ${contentLoopSectionFormattingBlockFragment}
  }
`

export const competitionBlockRecord = `
  ... on CompetitionBlockRecord {
    ${typeFragment}
    id
    competition {
      id
      competitionName
      competitionMode
      slug
      competitionId
      closingDatetime
      inIframe
    }
    ${contentLoopSectionFormattingBlockFragment}
  }
`

export const gridGalleryBlockRecord = `
  ... on GalleryGridRecord {
    ${typeFragment}
    id
    title {
      ${titleBlockFragment}
    }
    items {
      id
      link {
        ${linkBlock}
      }
      text {
        ${titleBlockFragment}
      }
      ${responsiveMediaFragment('image', '{ w: "600", h: "600" }')}
      ${responsiveMediaFragment('mobileImage', '{ w: "600", h: "600" }')}
      aspectRatio
      backgroundVideo {
        ${videoRecord}
      }
      backgroundMediaOverlay
      trackingId
    }
    trackingId
    lazyLoadImages
    ${contentLoopSectionFormattingBlockFragment}
  }
`

export const rowsFragment = `{
    ${headingRecord}
    ${dividerRecord}
    ${bannerRecord}
    ${videoRecord}
    ${standfirstRecord}
    ${multiColumnRecord}
    ${richTextRecord}
    ${carouselRecord}
    ${styledBannerRecord}
    ${sustainabilityBlockRecord}
    ${formBlockRecord}
    ${faqBlockRecord}
    ${anchorButtonRowRecord}
    ${competitionBlockRecord}
    ${dataCaptureBlockRecord}
    ${centeredCarouselRecord}
    ${mediaHeaderRecord}
    ${mediaHeroRecord}
    ${filmStripInteractiveHeroRecord}
    ${gridGalleryBlockRecord}
    ${emailSignupRecord}
    ${marketingRowRecord}
    ${instagramShopRecord}
    ${impactCarouselRecord}
    ${credibilityRowRecord}
    ${storeLocatorRowRecord}
    ${stickyCtaButtonRowRecord}
    ${textRevealRecord}
    ${interactiveListBlockRecord}
    ${stickyScrollListRecord}
    ${productCalloutSectionRecord}
    ${formHeroRecord}
    ${brandHeroRecord}
  }
`

export const builderFragment = `
  pageTheme
  rows ${rowsFragment}
  title
  ${seoFragment}
`

export const commerceTagFragment = `
  ... on CommerceTagRecord {
    title
    value
  }
`

export const localeFragment = 'fallbackLocales: en, locale: $locale'
